import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, FormControlLabel, Checkbox, IconButton, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { crearTramiteDeclaratoria, getTramitesDeclaratoria } from '../../services/declaratoriaService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import { esDniOCuilValido, mensajeDniOCuilInvalido } from '../../utils/documentoUtils';
import PageHeader from '../ui/PageHeader';

const VINCULOS = [
    { value: 'CONYUGE', label: 'Cónyuge' },
    { value: 'HIJO', label: 'Hijo/a' },
    { value: 'NIETO', label: 'Nieto/a' },
    { value: 'OTRO', label: 'Otro' },
];

const nuevoHeredero = () => ({ nombre: '', dni: '', vinculo: 'HIJO', fallecido: false, idHerederoPadre: null });

/**
 * Formulario para iniciar un trámite de Declaratoria de Herederos. Judicial (fuero provincial):
 * nunca pasa por ANSES. Carga el/los causante(s) y una carga rápida de herederos (el árbol
 * completo, con documentación por fila, se termina de editar después en la ficha con
 * TablaHerederos).
 */
export default function FormularioIniciarTramiteDeclaratoria({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        etiqueta: '',
        comentarios: '',
        causanteNombre: '',
        causanteDniCuil: '',
        causanteFechaFallecimiento: '',
        causantePadre: '',
        causanteMadre: '',
        clienteEsHeredero: false,
        clienteHerederoDni: '',
        clienteHerederoVinculo: 'HIJO',
    });
    const [segundoCausante, setSegundoCausante] = useState(false);
    const [causante2, setCausante2] = useState({
        causante2Nombre: '', causante2DniCuil: '', causante2FechaFallecimiento: '', causante2Padre: '', causante2Madre: '',
    });
    const [herederos, setHerederos] = useState([nuevoHeredero()]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    // El PACTO se emite acá, cuando el trámite todavía no existe: el hook lo adjunta recién
    // cuando hay idTramite (ver usePactosPendientes).
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Declaratoria duplicado para el mismo cliente ---
    const [tieneDuplicado, setTieneDuplicado] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Trámite de Declaratoria ya existente para el cliente seleccionado
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicado(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }
        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesDeclaratoria(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicado(items.length > 0);
                    if (items.length === 0) setConfirmarDuplicado(false);
                }
            } catch (err) {
                console.error('Error validando trámites de Declaratoria duplicados:', err);
                if (activo) { setTieneDuplicado(false); setCantidadDuplicados(0); }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };
        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeCausante2 = (e) => {
        const { name, value } = e.target;
        setCausante2(prev => ({ ...prev, [name]: value }));
    };

    const handleHerederoChange = (index, campo, valor) => {
        setHerederos(prev => prev.map((h, i) => (i === index ? { ...h, [campo]: valor } : h)));
    };

    const agregarHeredero = () => setHerederos(prev => [...prev, nuevoHeredero()]);

    const quitarHeredero = (index) => {
        setHerederos(prev => prev
            .filter((_, i) => i !== index)
            // Ningún otro heredero puede quedar apuntando (por índice) a la fila borrada.
            .map(h => (h.idHerederoPadre === index ? { ...h, idHerederoPadre: null } : h)));
    };

    // Hijos marcados como fallecidos: son los únicos padres válidos de un nieto.
    const opcionesPadreNieto = herederos
        .map((h, i) => ({ ...h, indice: i }))
        .filter(h => h.vinculo === 'HIJO' && h.fallecido);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (!form.causanteNombre || !form.causanteFechaFallecimiento) {
            setError('Debe indicar el nombre y la fecha de fallecimiento del causante.');
            return;
        }
        if (!esDniOCuilValido(form.causanteDniCuil)) {
            setError(mensajeDniOCuilInvalido('El DNI/CUIL del causante'));
            return;
        }
        if (segundoCausante && !esDniOCuilValido(causante2.causante2DniCuil)) {
            setError(mensajeDniOCuilInvalido('El DNI/CUIL del segundo causante'));
            return;
        }
        if (form.clienteEsHeredero && !esDniOCuilValido(form.clienteHerederoDni)) {
            setError(mensajeDniOCuilInvalido('El DNI del cliente como heredero'));
            return;
        }
        const herederoConDniInvalido = herederos.find(h => h.nombre?.trim() && !esDniOCuilValido(h.dni));
        if (herederoConDniInvalido) {
            setError(mensajeDniOCuilInvalido(`El DNI de "${herederoConDniInvalido.nombre.trim()}"`));
            return;
        }
        if (tieneDuplicado && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Declaratoria para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                etiqueta: form.etiqueta || null,
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
                causanteNombre: form.causanteNombre,
                causanteDniCuil: form.causanteDniCuil || null,
                causanteFechaFallecimiento: form.causanteFechaFallecimiento,
                causantePadre: form.causantePadre || null,
                causanteMadre: form.causanteMadre || null,
                clienteEsHeredero: form.clienteEsHeredero,
                clienteHerederoDni: form.clienteEsHeredero ? (form.clienteHerederoDni || null) : null,
                clienteHerederoVinculo: form.clienteEsHeredero ? form.clienteHerederoVinculo : null,
                herederos: herederos
                    .filter(h => h.nombre && h.nombre.trim() !== '')
                    .map(h => ({
                        nombre: h.nombre.trim(),
                        dni: h.dni || null,
                        vinculo: h.vinculo,
                        fallecido: h.fallecido,
                        idHerederoPadre: h.idHerederoPadre,
                    })),
                ...(segundoCausante ? causante2 : {}),
            };
            const creado = await crearTramiteDeclaratoria(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Declaratoria creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/declaratoria/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="diagram-3"
                        titulo="Iniciar Trámite de Declaratoria de Herederos"
                        descripcion="Trámite judicial (fuero provincial): complete los datos del causante y los herederos"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-declaratoria"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            {/* PACTO de honorarios, igual que en el resto de los altas. Al
                                adjuntarse deja además cargados los haberes pactados y el check
                                "Pacto OK" que la ficha muestra en el checklist. */}
                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />
                            <FormControlLabel
                                control={<Checkbox checked={form.clienteEsHeredero}
                                    onChange={(e) => setForm(prev => ({ ...prev, clienteEsHeredero: e.target.checked }))} />}
                                label="El cliente es uno de los herederos"
                            />
                            {form.clienteEsHeredero && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pl: 4 }}>
                                    <TextField
                                        label="Nombre"
                                        value={clienteSeleccionado ? apellidoNombre(clienteSeleccionado.apellido, clienteSeleccionado.nombre) : ''}
                                        disabled
                                        fullWidth
                                        helperText="Es el cliente seleccionado arriba."
                                    />
                                    <TextField
                                        label="DNI"
                                        value={form.clienteHerederoDni}
                                        onChange={(e) => setForm(prev => ({ ...prev, clienteHerederoDni: e.target.value }))}
                                        fullWidth
                                        error={!esDniOCuilValido(form.clienteHerederoDni)}
                                        helperText={!esDniOCuilValido(form.clienteHerederoDni) ? 'DNI inválido: debe tener 7 u 8 dígitos.' : ''}
                                    />
                                    <TextField
                                        select
                                        label="Vínculo con el causante"
                                        value={form.clienteHerederoVinculo}
                                        onChange={(e) => setForm(prev => ({ ...prev, clienteHerederoVinculo: e.target.value }))}
                                        fullWidth
                                    >
                                        {VINCULOS.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
                                    </TextField>
                                </Stack>
                            )}

                            {seccionTitulo('bi-person-x', 'Causante')}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Apellido y nombre" name="causanteNombre" value={form.causanteNombre}
                                    onChange={handleChange} required fullWidth
                                    helperText='Formato "APELLIDO, Nombre": se usa para sugerir la carátula judicial.' />
                                <TextField label="DNI / CUIL" name="causanteDniCuil" value={form.causanteDniCuil} onChange={handleChange} fullWidth
                                    error={!esDniOCuilValido(form.causanteDniCuil)}
                                    helperText={!esDniOCuilValido(form.causanteDniCuil) ? 'Debe ser un DNI (7 u 8 dígitos) o un CUIL/CUIT (11 dígitos).' : ''} />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Fecha de fallecimiento" name="causanteFechaFallecimiento" type="date"
                                    value={form.causanteFechaFallecimiento} onChange={handleChange} required fullWidth
                                    InputLabelProps={{ shrink: true }} />
                                <TextField label="Nombre del padre" name="causantePadre" value={form.causantePadre} onChange={handleChange} fullWidth />
                                <TextField label="Nombre de la madre" name="causanteMadre" value={form.causanteMadre} onChange={handleChange} fullWidth />
                            </Stack>

                            {!segundoCausante ? (
                                <Button variant="text" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start', color: '#9f1239' }}
                                    onClick={() => setSegundoCausante(true)}>
                                    Agregar segundo causante
                                </Button>
                            ) : (
                                <>
                                    <Divider textAlign="left">Segundo causante</Divider>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField label="Apellido y nombre" name="causante2Nombre" value={causante2.causante2Nombre}
                                            onChange={handleChangeCausante2} fullWidth />
                                        <TextField label="DNI / CUIL" name="causante2DniCuil" value={causante2.causante2DniCuil}
                                            onChange={handleChangeCausante2} fullWidth
                                            error={!esDniOCuilValido(causante2.causante2DniCuil)}
                                            helperText={!esDniOCuilValido(causante2.causante2DniCuil) ? 'Debe ser un DNI (7 u 8 dígitos) o un CUIL/CUIT (11 dígitos).' : ''} />
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField label="Fecha de fallecimiento" name="causante2FechaFallecimiento" type="date"
                                            value={causante2.causante2FechaFallecimiento} onChange={handleChangeCausante2} fullWidth
                                            InputLabelProps={{ shrink: true }} />
                                        <TextField label="Nombre del padre" name="causante2Padre" value={causante2.causante2Padre}
                                            onChange={handleChangeCausante2} fullWidth />
                                        <TextField label="Nombre de la madre" name="causante2Madre" value={causante2.causante2Madre}
                                            onChange={handleChangeCausante2} fullWidth />
                                    </Stack>
                                    <Button variant="text" color="error" size="small" sx={{ alignSelf: 'flex-start' }}
                                        onClick={() => setSegundoCausante(false)}>
                                        Quitar segundo causante
                                    </Button>
                                </>
                            )}

                            {seccionTitulo('bi-people', 'Herederos')}
                            <Typography variant="body2" color="text.secondary">
                                Carga rápida: el resto de la documentación (actas, DNI) se completa después en la ficha.
                                Para un nieto, seleccione de qué hijo fallecido desciende.
                            </Typography>
                            {herederos.map((h, index) => (
                                <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
                                    sx={{ pl: h.vinculo === 'NIETO' ? 3 : 0 }}>
                                    <TextField label="Nombre" value={h.nombre} onChange={(e) => handleHerederoChange(index, 'nombre', e.target.value)} fullWidth />
                                    <TextField label="DNI" value={h.dni} onChange={(e) => handleHerederoChange(index, 'dni', e.target.value)} sx={{ minWidth: 140 }}
                                        error={!esDniOCuilValido(h.dni)}
                                        helperText={!esDniOCuilValido(h.dni) ? 'DNI inválido' : ''} />
                                    <TextField select label="Vínculo" value={h.vinculo} sx={{ minWidth: 140 }}
                                        onChange={(e) => handleHerederoChange(index, 'vinculo', e.target.value)}>
                                        {VINCULOS.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
                                    </TextField>
                                    {h.vinculo === 'HIJO' && (
                                        <FormControlLabel
                                            control={<Checkbox checked={h.fallecido} onChange={(e) => handleHerederoChange(index, 'fallecido', e.target.checked)} />}
                                            label="Falleció"
                                        />
                                    )}
                                    {h.vinculo === 'NIETO' && (
                                        <TextField select label="Hijo de" value={h.idHerederoPadre ?? ''} sx={{ minWidth: 180 }}
                                            onChange={(e) => handleHerederoChange(index, 'idHerederoPadre', e.target.value === '' ? null : Number(e.target.value))}>
                                            <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                            {opcionesPadreNieto.map(p => (
                                                <MenuItem key={p.indice} value={p.indice}>{p.nombre || `Hijo #${p.indice + 1}`}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                    <IconButton color="error" onClick={() => quitarHeredero(index)} aria-label="Quitar heredero">
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            ))}
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={agregarHeredero} sx={{ alignSelf: 'flex-start' }}>
                                Agregar heredero
                            </Button>

                            {seccionTitulo('bi-file-earmark-text', 'Datos adicionales')}
                            <TextField label="Etiqueta / referencia" name="etiqueta" value={form.etiqueta} onChange={handleChange} fullWidth />

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Particularidades del caso (lo que se le deja anotado al equipo)." />

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicado && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Declaratoria' : `${cantidadDuplicados} trámites de Declaratoria`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicado && (
                                <FormControlLabel
                                    control={<Checkbox checked={confirmarDuplicado} onChange={(e) => setConfirmarDuplicado(e.target.checked)} />}
                                    label="Confirmo que deseo continuar con un nuevo trámite de Declaratoria igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Declaratoria activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary-hover)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
